/**
 * Static feature flags configuration.
 * These flags control feature availability across the application.
 */
const FEATURE_FLAGS = {
  /**
   * List of place UUIDs for which plan navigation (clicking on locations) and highlighting is enabled.
   * For places not in this list, location circles will be removed from SVG plans.
   */
  navigablePlansPlaces: [
    'e69837ac-9486-47a8-b1aa-c0ffd7a04f1e',
    'ee0bc4cd-6c50-49cc-8da5-94c0d8d22835'
  ] as string[],

  /**
   * Focal point related feature flags.
   */
  focalPoints: {
    /**
     * Whether focal points are taken into account in gallery/places listings etc.
     */
    enabledInListings: false,
    /**
     * Whether it's possible to manually override a focal point in the admin panel.
     */
    manualOverrideEnabled: false,
  },
} as const;

type FeatureFlagKey = keyof typeof FEATURE_FLAGS;
type FeatureFlagValue<K extends FeatureFlagKey> = typeof FEATURE_FLAGS[K];

/**
 * Get a feature flag value by key.
 * Type-safe accessor that returns the correct type for each flag.
 */
export function getFeatureFlag<K extends FeatureFlagKey>(key: K): FeatureFlagValue<K> {
  return FEATURE_FLAGS[key];
}

/**
 * Check if a feature flag is enabled (for boolean flags).
 */
export function isFeatureEnabled(key: FeatureFlagKey): boolean {
  const value = FEATURE_FLAGS[key];
  return typeof value === 'boolean' ? value : false;
}

/**
 * Check if a place has navigable plans enabled.
 */
export function isPlaceNavigable(placeId: string | undefined): boolean {
  if (!placeId) return false;
  const navigablePlaces = FEATURE_FLAGS.navigablePlansPlaces;
  return navigablePlaces.length === 0 || navigablePlaces.includes(placeId);
}

